import { CheckCircle2, ChevronDown, ClipboardList, Clock3, ExternalLink, EyeOff, FileText, Flag, Globe2, Layers, Lock, MapPin, Megaphone, MessageSquare, Search, Send, ShieldCheck, UsersRound, Vote, X } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { RichTextEditor } from "./RichTextEditor";
import { Avatar } from "./ui";
import { canCreatePostType, confidentialityLevels, jurisdictionTags, normalizePost, postTypes, validatePostDraft } from "../lib/classification";
import type { ActionLabel, ConfidentialityLevel, DiscussionType, JurisdictionTag, LexData, PostType, Visibility, WorkflowStatus } from "../lib/types";

export type CreatePostInput = {
  title: string;
  body: string;
  type: DiscussionType;
  postType: PostType;
  practiceAreaIds: string[];
  topicTags: string[];
  jurisdictionTags: JurisdictionTag[];
  confidentialityLevel: ConfidentialityLevel;
  workflowStatus: WorkflowStatus;
  actionLabels: ActionLabel[];
  visibility: Visibility;
};

export type CreatePostPersistence = {
  createPost?: (input: CreatePostInput) => Promise<unknown>;
};

const composablePostTypes = postTypes.filter((type) => type !== "Amendment" && type !== "Vote");

const JURISDICTION_CODES: Record<string, string> = {
  Luxembourg: "LU", Belgium: "BE", France: "FR", Germany: "DE", Netherlands: "NL", Italy: "IT", Spain: "ES", "United Kingdom": "UK", Switzerland: "CH", "United States": "US",
};

export function CreatePostModal({
  data,
  initialType = "Question",
  draftId,
  onClose,
  onCreated,
  persistence,
}: {
  data: LexData;
  initialType?: PostType;
  draftId?: string | null;
  onClose: () => void;
  onCreated: (message: string) => void;
  persistence?: CreatePostPersistence;
}) {
  const composeTopicOptions = useMemo(() => [...new Set([...data.practiceAreas.filter((area) => area.active).map((area) => area.name), "Other"])], [data.practiceAreas]);
  const postTypeOptions = useMemo(() => composablePostTypes.map(optionForPostType), []);
  const confidentialityOptions = useMemo(() => confidentialityLevels.map(optionForConfidentiality), []);

  const [postType, setPostType] = useState<PostType>((composablePostTypes as PostType[]).includes(initialType) ? initialType : "Question");
  const [confidentiality, setConfidentiality] = useState<ConfidentialityLevel>("Network-wide");
  const [topics, setTopics] = useState<string[]>([composeTopicOptions[0] ?? "Other"]);
  const [jurisdictions, setJurisdictions] = useState<JurisdictionTag[]>(["EU"]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!draftId) return;
    const draft = data.discussions.find((item) => item.id === draftId);
    if (!draft) return;
    const classification = normalizePost(draft, data);
    setPostType(classification.postType);
    setConfidentiality(classification.confidentialityLevel);
    setTopics(classification.topicTags);
    setJurisdictions(classification.jurisdictionTags);
    setTitle(draft.title);
    setBody(draft.body);
  }, [data, draftId]);

  const topic = topics[0] ?? "";
  const setTopic = (value: string) => setTopics(value ? [value] : []);
  const toggleJurisdiction = (tag: JurisdictionTag) => {
    setJurisdictions((current) => (current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]));
  };

  const postTypeOption = postTypeOptions.find((option) => option.value === postType) ?? postTypeOptions[0];
  const confidentialityOption = confidentialityOptions.find((option) => option.value === confidentiality) ?? confidentialityOptions[0];
  const firmName = data.viewerFirm?.name ?? data.viewer.name;
  const readinessFields = [Boolean(title.trim()), Boolean(body.trim()), topics.length > 0, jurisdictions.length > 0, true, true];
  const readiness = Math.round((readinessFields.filter(Boolean).length / readinessFields.length) * 100);
  const ready = Boolean(title.trim() && body.trim());

  const saveDraft = () => {
    onCreated(`Draft saved: ${title.trim() || postType}.`);
    onClose();
  };

  const submit = async () => {
    const cleanTitle = title.trim();
    const cleanBody = body.trim();
    const actionLabels = actionLabelsForPostType(postType);
    const workflowStatus = workflowStatusForPostType(postType);
    const errors = validatePostDraft({
      postType, title: cleanTitle, body: cleanBody, summary: cleanBody, fullText: cleanBody,
      confidentialityLevel: confidentiality, topicTags: topics, jurisdictionTags: jurisdictions, actionLabels,
    });
    if (!cleanTitle) errors.unshift("Title is required.");
    if (!cleanBody) errors.unshift("Body is required.");
    if (!canCreatePostType({ membershipCategory: data.viewerFirm?.membershipCategory ?? "Observer", postType })) {
      errors.unshift(`${data.viewerFirm?.membershipCategory ?? "Observer"} accounts cannot create ${postType.toLowerCase()} posts.`);
    }
    const createdByFirmId = data.viewerFirm?.id ?? data.viewer.firmId ?? "";
    if (!createdByFirmId) errors.unshift("A firm account is required to create a post.");
    if (errors.length > 0) { setError(errors[0]); return; }

    const input: CreatePostInput = {
      title: cleanTitle, body: cleanBody, type: discussionTypeForPostType(postType), postType,
      practiceAreaIds: practiceAreaIdsForTopics(data, topics), topicTags: topics, jurisdictionTags: jurisdictions,
      confidentialityLevel: confidentiality, workflowStatus, actionLabels, visibility: visibilityForConfidentiality(confidentiality),
    };
    try {
      if (persistence?.createPost) await persistence.createPost(input);
      onCreated(`Created ${postType.toLowerCase()}: ${cleanTitle}.`);
      onClose();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not create the post.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-navy/45 p-3 backdrop-blur-[2px]" role="dialog" aria-modal="true">
      <div className="flex max-h-[95vh] w-[min(1200px,97vw)] flex-col overflow-hidden rounded-2xl bg-surface shadow-soft">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-line px-6 py-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-line bg-surface"><MosaicMark /></span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[22px] font-semibold leading-tight text-ink">Create post</h2>
            <p className="text-[13.5px] text-secondary">Classify it once so the right members see it.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink"><X className="h-4 w-4" /></button>
        </div>

        {/* Body */}
        <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0 overflow-y-auto px-6 py-5">
            {error ? <div className="mb-4 rounded-lg border border-danger/30 bg-[#FFF7F4] px-3 py-2 text-sm text-danger">{error}</div> : null}

            <div className="grid gap-4 md:grid-cols-2">
              <FriendlySelect label="Post type" value={postType} options={postTypeOptions} onChange={(value) => setPostType(value as PostType)} />
              <FriendlySelect label="Confidentiality" value={confidentiality} options={confidentialityOptions} onChange={(value) => setConfidentiality(value as ConfidentialityLevel)} />
            </div>

            <div className="mt-5">
              <FieldLabel>Title</FieldLabel>
              <div className="relative">
                <input maxLength={90} className="min-h-12 w-full rounded-xl border border-line bg-surface px-4 pr-14 text-[15px] outline-none placeholder:text-muted focus:border-primary" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What would you like members to consider?" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-[11px] text-muted">{title.length}/90</span>
              </div>
            </div>

            <div className="mt-5">
              <RichTextEditor compact label="Body" value={body} onChange={setBody} placeholder="Add enough context for members to respond — leave out client names and anything privileged." />
            </div>

            <div className="mt-5">
              <FieldLabel hint="choose one">Topic</FieldLabel>
              <TopicPicker options={composeTopicOptions} value={topic} onChange={setTopic} />
            </div>

            <div className="mt-5">
              <FieldLabel hint="select all that apply">Jurisdiction</FieldLabel>
              <JurisdictionGrid values={jurisdictionTags} selected={jurisdictions} onToggle={toggleJurisdiction} />
            </div>
          </div>

          {/* Preview */}
          <aside className="hidden min-w-0 overflow-y-auto border-l border-line bg-canvas px-5 py-5 lg:block">
            <div className="mb-3 flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary"><span className="h-2 w-2 rounded-full bg-primary" /> Preview</div>
            <div className="rounded-xl border border-line bg-surface p-4">
              <div className="flex items-center gap-2.5">
                <Avatar name={firmName} imageUrl={data.viewerFirm?.logoUrl ?? data.viewerFirm?.avatarUrl} size="sm" shape="rounded" />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-ink">{firmName}</div>
                  <div className="text-[12px] text-secondary">posting to the network</div>
                </div>
              </div>
              <span className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-tint px-2 py-1 font-mono text-[11px] font-medium text-primary">{postTypeOption.previewIcon} {postTypeOption.label}</span>
              <div className={`mt-2.5 text-[17px] font-semibold leading-snug ${title ? "text-ink" : "text-muted"}`}>{title || "Your post title appears here…"}</div>
              <p className="mt-1 text-[13px] italic leading-5 text-muted">{body ? stripHtml(body).slice(0, 160) : "Body preview will appear as you write."}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {topics.map((tag) => <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-goodBorder bg-goodBg px-2 py-0.5 text-[11.5px] text-good"><FileText className="h-3 w-3" /> {tag}</span>)}
                {jurisdictions.map((tag) => <span key={tag} className="inline-flex items-center gap-1 rounded-full border border-infoBorder bg-infoBg px-2 py-0.5 text-[11.5px] text-navy"><MapPin className="h-3 w-3" /> {tag}</span>)}
              </div>
              <div className="mt-4 flex items-start gap-2.5 border-t border-divider pt-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-divider text-secondary">{confidentialityOption.previewIcon}</span>
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-ink">{confidentialityOption.label}</div>
                  <div className="text-[12px] text-secondary">{confidentialityOption.description}</div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-secondary">Appears as</div>
              <div className="flex flex-wrap gap-1.5">
                <span className="rounded-md border border-warnBorder bg-warnBg/60 px-2 py-0.5 text-[11.5px] font-medium text-warn">{postType}</span>
                {topics.map((tag) => <span key={tag} className="rounded-md border border-goodBorder bg-goodBg px-2 py-0.5 text-[11.5px] font-medium text-good">{tag}</span>)}
                {jurisdictions.map((tag) => <span key={tag} className="rounded-md border border-infoBorder bg-infoBg px-2 py-0.5 text-[11.5px] font-medium text-navy">{tag}</span>)}
                <span className="rounded-md border border-line bg-quote px-2 py-0.5 text-[11.5px] font-medium text-quoteText">{confidentiality}</span>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-line bg-surface p-3">
              <div className="flex items-center justify-between text-[12.5px] font-semibold text-ink"><span className="font-mono uppercase tracking-[0.08em] text-secondary">Ready to post</span><span>{readiness}%</span></div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-track"><div className="h-full rounded-full bg-primary" style={{ width: `${readiness}%` }} /></div>
            </div>
          </aside>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 border-t border-line px-6 py-4">
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-secondary"><ShieldCheck className="h-4 w-4 text-good" /> Classification controls who sees this</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={saveDraft} className="inline-flex min-h-11 items-center rounded-lg border border-line bg-surface px-4 text-[14px] font-semibold text-ink hover:bg-hover">Save draft</button>
            <button type="button" onClick={() => void submit()} disabled={!ready} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-[14px] font-semibold text-white hover:bg-[#6a1925] disabled:cursor-not-allowed disabled:opacity-50"><Send className="h-4 w-4" /> Post</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MosaicMark() {
  return (
    <span className="grid grid-cols-2 gap-0.5">
      {["bg-primary", "bg-[#c9bfb0]", "bg-[#c9bfb0]", "bg-primary"].map((cls, index) => <span key={index} className={`h-2.5 w-2.5 rounded-[2px] ${cls}`} />)}
    </span>
  );
}

function FieldLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-secondary">{children}{hint ? <span className="ml-1.5 font-medium normal-case tracking-normal text-muted">· {hint}</span> : null}</div>;
}

type FriendlyOption = { value: string; label: string; description: string; icon: ReactNode; previewIcon: ReactNode };

function FriendlySelect({ label, value, options, onChange }: { label: string; value: string; options: FriendlyOption[]; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => option.value === value) ?? options[0];
  return (
    <div className="relative">
      <FieldLabel>{label}</FieldLabel>
      <button type="button" className={`flex min-h-14 w-full items-center gap-3 rounded-xl border bg-surface px-4 text-left outline-none ${open ? "border-primary shadow-[0_0_0_3px_rgba(122,28,44,0.14)]" : "border-line hover:bg-hover"}`} onClick={() => setOpen((current) => !current)} aria-expanded={open}>
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-tint text-primary">{selected?.icon}</span>
        <span className="min-w-0 flex-1"><span className="block text-[15px] font-semibold text-ink">{selected?.label ?? value}</span><span className="block text-[12.5px] text-secondary">{selected?.description}</span></span>
        <ChevronDown className={`h-5 w-5 text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open ? (
        <>
          <button type="button" aria-hidden tabIndex={-1} className="fixed inset-0 z-30 cursor-default" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 rounded-xl border border-line bg-surface p-1.5 shadow-soft">
            {options.map((option) => {
              const active = option.value === value;
              return (
                <button key={option.value} type="button" className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left ${active ? "bg-tint" : "hover:bg-hover"}`} onClick={() => { onChange(option.value); setOpen(false); }}>
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${active ? "bg-primary text-white" : "bg-divider text-secondary"}`}>{option.icon}</span>
                  <span className="min-w-0 flex-1"><span className={`block text-[14px] font-semibold ${active ? "text-primary" : "text-ink"}`}>{option.label}</span><span className="block text-[12.5px] text-secondary">{option.description}</span></span>
                  {active ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}

function TopicPicker({ options, value, onChange }: { options: string[]; value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = options.filter((option) => option.toLowerCase().includes(query.toLowerCase()) && option !== value);
  return (
    <div className="relative">
      <div className={`flex min-h-12 items-center gap-2 rounded-xl border bg-surface px-3 ${open ? "border-primary" : "border-line"}`}>
        <Search className="h-4 w-4 shrink-0 text-muted" />
        {value ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-2 py-1 text-[13px] font-semibold text-white">{value}<button type="button" onClick={() => onChange("")} aria-label="Clear topic"><X className="h-3 w-3" /></button></span>
        ) : null}
        <input value={query} onFocus={() => setOpen(true)} onChange={(event) => { setQuery(event.target.value); setOpen(true); }} placeholder={value ? "Change topic…" : "Search topics…"} className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted" />
      </div>
      {open ? (
        <>
          <button type="button" aria-hidden tabIndex={-1} className="fixed inset-0 z-30 cursor-default" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-60 overflow-y-auto rounded-xl border border-line bg-surface p-1.5 shadow-soft">
            {filtered.length > 0 ? filtered.map((option) => (
              <button key={option} type="button" className="block w-full rounded-lg px-3 py-2 text-left text-[14px] text-ink hover:bg-hover" onClick={() => { onChange(option); setQuery(""); setOpen(false); }}>{option}</button>
            )) : <div className="px-3 py-2 text-[13px] text-muted">No matching topics.</div>}
          </div>
        </>
      ) : null}
    </div>
  );
}

function JurisdictionGrid({ values, selected, onToggle }: { values: JurisdictionTag[]; selected: JurisdictionTag[]; onToggle: (value: JurisdictionTag) => void }) {
  const [query, setQuery] = useState("");
  const filtered = values.filter((value) => value.toLowerCase().includes(query.toLowerCase()));
  return (
    <div>
      <div className="mb-3 flex min-h-11 items-center gap-2 rounded-xl border border-line bg-surface px-3">
        <Search className="h-4 w-4 shrink-0 text-muted" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter countries…" className="min-w-0 flex-1 bg-transparent text-[14px] outline-none placeholder:text-muted" />
      </div>
      <div className="flex flex-wrap gap-2">
        {filtered.map((value) => {
          const active = selected.includes(value);
          const code = JURISDICTION_CODES[value];
          return (
            <button key={value} type="button" onClick={() => onToggle(value)} className={`inline-flex min-h-9 items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium transition ${active ? "border-primary bg-primary text-white" : "border-line bg-surface text-ink hover:bg-hover"}`}>
              {active ? <span className="text-[11px]">✓</span> : null}
              {value}
              {code ? <span className={`font-mono text-[10px] ${active ? "text-white/70" : "text-muted"}`}>{code}</span> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function optionForPostType(value: PostType): FriendlyOption {
  const make = (label: string, description: string, icon: ReactNode): FriendlyOption => ({ value, label, description, icon, previewIcon: icon });
  if (value === "Question") return make("Question", "Ask the network for views", <MessageSquare className="h-5 w-5" />);
  if (value === "Discussion") return make("Note", "Share an update — replies optional", <FileText className="h-5 w-5" />);
  if (value === "Proposal") return make("Joint position", "Draft a stance to co-sign", <Flag className="h-5 w-5" />);
  if (value === "Resource") return make("Resource", "Share a model, memo or reference", <ClipboardList className="h-5 w-5" />);
  if (value === "Update") return make("Update", "Inform members without workflow", <Megaphone className="h-5 w-5" />);
  if (value === "Event") return make("Event", "Announce a meeting or session", <Clock3 className="h-5 w-5" />);
  if (value === "Consultation") return make("Consultation", "Request input on a file", <UsersRound className="h-5 w-5" />);
  if (value === "Poll") return make("Poll", "Collect a quick preference", <Vote className="h-5 w-5" />);
  if (value === "Task") return make("Task", "Ask members to help", <CheckCircle2 className="h-5 w-5" />);
  return make(value, "Publish a member update", <Megaphone className="h-5 w-5" />);
}

function optionForConfidentiality(value: ConfidentialityLevel): FriendlyOption {
  const make = (label: string, description: string, icon: ReactNode): FriendlyOption => ({ value, label, description, icon, previewIcon: icon });
  if (value === "Network-wide") return make("Network-wide", "All member firms can see and reply", <Globe2 className="h-5 w-5" />);
  if (value === "Working group only") return make("Workspace only", "Only the relevant workspace sees it", <Layers className="h-5 w-5" />);
  if (value === "Full Members only") return make("Full members", "Limited to full member firms", <ShieldCheck className="h-5 w-5" />);
  if (value === "Committee only") return make("Committee only", "Restricted to workspace managers", <Lock className="h-5 w-5" />);
  if (value === "Invited participants only") return make("Private draft", "Only invited participants can see it", <EyeOff className="h-5 w-5" />);
  return make("Public/exportable", "Suitable for public-site reuse", <ExternalLink className="h-5 w-5" />);
}

function discussionTypeForPostType(postType: PostType): DiscussionType {
  if (postType === "Question") return "question";
  if (postType === "Proposal") return "governance_proposal";
  if (postType === "Consultation") return "legislative_comment";
  return "practice_note";
}

function visibilityForConfidentiality(level: ConfidentialityLevel): Visibility {
  if (level === "Committee only") return "committee";
  if (level === "Full Members only") return "members";
  if (level === "Invited participants only" || level === "Working group only") return "firm";
  return "network";
}

function workflowStatusForPostType(postType: PostType): WorkflowStatus {
  if (postType === "Question" || postType === "Consultation") return "Input requested";
  if (postType === "Proposal") return "Submitted";
  return "Open";
}

function actionLabelsForPostType(postType: PostType): ActionLabel[] {
  if (postType === "Question" || postType === "Consultation") return ["Input requested"];
  if (postType === "Proposal") return ["Review requested"];
  if (postType === "Task") return ["Volunteer requested"];
  return ["No action required"];
}

function practiceAreaIdsForTopics(data: LexData, selectedTopics: string[]) {
  const exactMatches = data.practiceAreas.filter((area) => selectedTopics.includes(area.name)).map((area) => area.id);
  if (exactMatches.length > 0) return exactMatches;
  const viewerPracticeIds = data.viewerFirm
    ? data.firmPracticeAreas.filter((item) => item.firmId === data.viewerFirm?.id).map((item) => item.practiceAreaId)
    : [];
  return viewerPracticeIds.length > 0 ? viewerPracticeIds.slice(0, 1) : data.practiceAreas.slice(0, 1).map((area) => area.id);
}
